import React, { useState, useEffect } from 'react';

export interface DiagnosticStep {
  id: string;
  label: string;
  check: () => Promise<{
    passed: boolean;
    details?: any;
    error?: string;
    timing?: number;
  }>;
  critical?: boolean;
}

interface FeatureDebuggerProps {
  title: string;
  steps: DiagnosticStep[];
  children?: React.ReactNode;
  autoRun?: boolean;
  onComplete?: (results: StepResult[]) => void;
}

interface StepResult {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'passed' | 'failed' | 'skipped';
  details?: any;
  error?: string;
  timing?: number;
}

export const FeatureDebugger: React.FC<FeatureDebuggerProps> = ({
  title,
  steps,
  children,
  autoRun = false,
  onComplete
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<StepResult[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(-1);

  useEffect(() => {
    if (autoRun && isOpen) {
      runDiagnostics();
    }
  }, [isOpen, autoRun]);

  const runDiagnostics = async () => {
    setIsRunning(true);
    setCurrentStep(0);
    const newResults: StepResult[] = [];

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      setCurrentStep(i);

      setResults([...newResults, {
        id: step.id,
        label: step.label,
        status: 'running',
      }]);

      const startTime = performance.now();

      try {
        const result = await step.check();
        const timing = performance.now() - startTime;

        const stepResult: StepResult = {
          id: step.id,
          label: step.label,
          status: result.passed ? 'passed' : 'failed',
          details: result.details,
          error: result.error,
          timing
        };

        newResults.push(stepResult);
        setResults([...newResults]);

        if (!result.passed && step.critical) {
          for (let j = i + 1; j < steps.length; j++) {
            newResults.push({
              id: steps[j].id,
              label: steps[j].label,
              status: 'skipped'
            });
          }
          setResults([...newResults]);
          break;
        }
      } catch (error) {
        const timing = performance.now() - startTime;
        const stepResult: StepResult = {
          id: step.id,
          label: step.label,
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error',
          timing
        };

        newResults.push(stepResult);
        setResults([...newResults]);

        if (step.critical) {
          for (let j = i + 1; j < steps.length; j++) {
            newResults.push({
              id: steps[j].id,
              label: steps[j].label,
              status: 'skipped'
            });
          }
          setResults([...newResults]);
          break;
        }
      }
    }

    setIsRunning(false);
    setCurrentStep(-1);
    onComplete?.(newResults);
  };

  const getStatusIcon = (status: StepResult['status']) => {
    switch (status) {
      case 'pending': return '⏸';
      case 'running': return '⏳';
      case 'passed': return '✅';
      case 'failed': return '❌';
      case 'skipped': return '⏭️';
      default: return '❓';
    }
  };

  const getStatusColor = (status: StepResult['status']) => {
    switch (status) {
      case 'pending': return 'text-gray-500';
      case 'running': return 'text-yellow-500 animate-pulse';
      case 'passed': return 'text-green-500';
      case 'failed': return 'text-red-500';
      case 'skipped': return 'text-gray-400';
      default: return 'text-gray-500';
    }
  };

  const failedStep = results.find(r => r.status === 'failed');
  const passedCount = results.filter(r => r.status === 'passed').length;
  const totalTime = results.reduce((sum, r) => sum + (r.timing || 0), 0);

  return (
    <div className="relative">
      <div
        className="fixed bottom-4 right-4 z-50 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl p-2 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🔬</span>
          <span className="text-xs text-gray-400">{title}</span>
          {failedStep && (
            <span className="text-xs text-red-500">
              Failed at step {results.indexOf(failedStep) + 1}/{steps.length}
            </span>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  🔬 {title} Diagnostic
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {failedStep && (
                <div className="mt-2 p-2 bg-red-900/20 border border-red-800 rounded text-sm">
                  <strong className="text-red-400">Failed at step {results.indexOf(failedStep) + 1}/{steps.length}:</strong>
                  <div className="text-red-300">{failedStep.label}</div>
                  {failedStep.error && (
                    <div className="text-red-200 text-xs mt-1">Error: {failedStep.error}</div>
                  )}
                  {failedStep.timing && (
                    <div className="text-red-200 text-xs">After {failedStep.timing.toFixed(1)}ms</div>
                  )}
                </div>
              )}

              {!isRunning && results.length > 0 && !failedStep && (
                <div className="mt-2 p-2 bg-green-900/20 border border-green-800 rounded text-sm text-green-400">
                  ✅ All {passedCount} checks passed in {totalTime.toFixed(1)}ms
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {steps.map((step, index) => {
                  const result = results.find(r => r.id === step.id);
                  const status = result?.status || 'pending';
                  const isCurrentStep = index === currentStep;

                  return (
                    <div
                      key={step.id}
                      className={`p-3 rounded-lg border ${
                        isCurrentStep ? 'border-yellow-600 bg-yellow-900/10' : 'border-gray-700 bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={getStatusColor(status)}>
                            {getStatusIcon(status)}
                          </span>
                          <span className="text-sm text-gray-300">
                            Step {index + 1}: {step.label}
                          </span>
                          {step.critical && (
                            <span className="text-xs bg-yellow-900/50 text-yellow-500 px-1 rounded">
                              CRITICAL
                            </span>
                          )}
                        </div>
                        {result?.timing && (
                          <span className="text-xs text-gray-500">
                            {result.timing.toFixed(1)}ms
                          </span>
                        )}
                      </div>

                      {result?.details && (
                        <div className="mt-2 text-xs text-gray-400 font-mono">
                          <pre className="whitespace-pre-wrap">
                            {JSON.stringify(result.details, null, 2)}
                          </pre>
                        </div>
                      )}

                      {result?.error && (
                        <div className="mt-2 text-xs text-red-400">
                          {result.error}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-4 border-t border-gray-700">
              <button
                onClick={runDiagnostics}
                disabled={isRunning}
                className={`px-4 py-2 rounded font-medium transition-colors ${
                  isRunning
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {isRunning ? '🔬 Running Diagnostics...' : '🚀 Run Diagnostics'}
              </button>

              {results.length > 0 && (
                <span className="ml-4 text-sm text-gray-400">
                  {passedCount}/{steps.length} passed
                  {totalTime > 0 && ` • ${totalTime.toFixed(1)}ms total`}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {children}
    </div>
  );
};